/**
 * Parse an API timestamp as an instant in time.
 *
 * Current API responses include `Z` or an explicit offset. Older SQLite-backed
 * responses omitted it even though the stored value was UTC. Keep accepting
 * those values so a cached response cannot briefly show server time after a
 * deployment.
 */
export function parseApiTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null

  const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(value)
  const parsed = new Date(hasTimezone ? value : `${value}Z`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** Format an API timestamp in the user's browser locale and timezone. */
export function formatLocalDateTime(
  value: string | null | undefined,
  locale?: Intl.LocalesArgument,
  timeZone?: string,
): string {
  const when = parseApiTimestamp(value)
  if (!when) return ''

  const now = new Date()
  const thisYear = yearInTimeZone(when, timeZone) === yearInTimeZone(now, timeZone)
  return when.toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    ...(thisYear ? {} : { year: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  })
}

/** Format an API timestamp as a calendar date in the user's local timezone. */
export function formatLocalDate(
  value: string | null | undefined,
  locale?: Intl.LocalesArgument,
  timeZone?: string,
): string {
  const when = parseApiTimestamp(value)
  if (!when) return ''

  return when.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  })
}

function yearInTimeZone(value: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat('en', {
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).format(value)
}
